// version 27.7. 21:45

import { c1_state } from "./C1_runtimeContext";
import { axesForFloor, floorsForAxis } from "./C1_axisFloorScope";
import { C1_Axis } from "./C1_Axis";
import { C1_Floor } from "./C1_Floor";
import { C1_ModelSettings } from "./C1_ModelSettings";
import { migrateC1PdfSettings } from "./C1_migratePdfSettings";
import { C1_Products } from "./C1_Products";
import type {
  C1_AxisData,
  C1_FloorData,
  C1_ProjectData,
  C1_HouseType,
  C1_EquipmentStandard,
  C1_SelectedProductManufacturers,
  C1_MasterPdfConfigData,
  WithOptionalId,
} from "../types/C1_ModelTypes";
import type { Rect } from "../types/C1_AppTypes";
import type ProjectMetadata from "../../../src/models/ProjectMetadata";
import WatchableValue from "../../../src/models/WatchableValue";
import type { C1_DisplayMode } from "../types/C1_ModelTypes";
import Floor from "../../../src/models/Floor";
import BaseModel from "../../../src/models/BaseModel";
import type BaseModelMap from "../../../src/models/BaseModelMap";
import type TemplateGroup from "../../../src/models/TemplateGroup";

interface C1_ProjectRootData {
  tagNumbering?: import("../estada/C1_frozenTags").C1_TagNumbering;
  id: string;
  updatedAt: string;
  northAngle: number;
  houseType: C1_HouseType;
  buildingWidth: number;
  buildingLength: number;
  floorDistance: number;
  equipmentStandard: C1_EquipmentStandard;
  selectedProductManufacturers: C1_SelectedProductManufacturers;
  masterPdfConfig?: C1_MasterPdfConfigData;
  currentFloorId?: string;
}

function axisLabels(axes: C1_AxisData[]) {
  const labels = new Map<string, string>();
  const verticalAxes = sortedAxesOfKind(axes, "v");
  const horizontalAxes = sortedAxesOfKind(axes, "h");

  for (let index = 0; index < verticalAxes.length; index += 1) {
    labels.set(verticalAxes[index].id, String.fromCharCode(65 + (index % 26)));
  }
  for (let index = 0; index < horizontalAxes.length; index += 1) {
    labels.set(horizontalAxes[index].id, String(index + 1));
  }
  return labels;
}

/** Returns axes of one kind ordered by position without callback-based array transformations. */
function sortedAxesOfKind(axes: C1_AxisData[], kind: C1_AxisData["kind"]): C1_AxisData[] {
  const sortedAxes: C1_AxisData[] = [];
  for (const axis of axes) {
    if (axis.kind !== kind) continue;
    let insertionIndex = sortedAxes.length;
    while (insertionIndex > 0 && sortedAxes[insertionIndex - 1].pos > axis.pos) insertionIndex -= 1;
    sortedAxes.splice(insertionIndex, 0, axis);
  }
  return sortedAxes;
}

/** Includes room contours even when imported or diagonal walls have no matching axis. */
function calculateProjectBounds(
  axes: Iterable<C1_Axis>,
  points: Iterable<{ x: number; y: number }>,
): Rect {
  let x1 = Number.POSITIVE_INFINITY;
  let y1 = Number.POSITIVE_INFINITY;
  let x2 = Number.NEGATIVE_INFINITY;
  let y2 = Number.NEGATIVE_INFINITY;

  for (const axis of axes) {
    if (axis.kind === "v") {
      if (axis.pos < x1) x1 = axis.pos;
      if (axis.pos > x2) x2 = axis.pos;
    } else {
      if (axis.pos < y1) y1 = axis.pos;
      if (axis.pos > y2) y2 = axis.pos;
    }
  }

  for (const point of points) {
    if (Number.isFinite(point.x)) {
      x1 = Math.min(x1, point.x);
      x2 = Math.max(x2, point.x);
    }
    if (Number.isFinite(point.y)) {
      y1 = Math.min(y1, point.y);
      y2 = Math.max(y2, point.y);
    }
  }

  return {
    x1: Number.isFinite(x1) ? x1 : 0,
    y1: Number.isFinite(y1) ? y1 : 0,
    x2: Number.isFinite(x2) ? x2 : 10,
    y2: Number.isFinite(y2) ? y2 : 8,
  };
}

export class C1_Project extends BaseModel {
  protected _data: C1_ProjectRootData;

  get projectMetadata(): ProjectMetadata {
    return state.projectMetadata;
  }
  readonly displayMode = new WatchableValue<C1_DisplayMode>("design", "displayMode");

  constructor(data: Partial<C1_ProjectRootData>, templateGroups: BaseModelMap<TemplateGroup>) {
    super();
    this._data = Object.assign(
      {
        id: "",
        updatedAt: new Date().toISOString(),
        northAngle: 0,
        houseType: "detachedHouse" as const,
        buildingWidth: 12,
        buildingLength: 12,
        floorDistance: 3,
        equipmentStandard: 4 as const,
        selectedProductManufacturers: {},
      } satisfies C1_ProjectRootData,
      data,
    );
    if (this._data.id) this.id = this._data.id;
    this.settings = new C1_ModelSettings({}, this);
    this.products = new C1_Products(templateGroups);
    this.displayMode.onChange.subscribe(() => this.changed("displayMode"));
  }

  get data(): Readonly<C1_ProjectRootData> {
    return this._data;
  }

  settings!: C1_ModelSettings;
  setTagNumbering(value: import("../estada/C1_frozenTags").C1_TagNumbering) {
    if (JSON.stringify(value) === JSON.stringify(this._data.tagNumbering)) return;
    this.beforeChange("tagNumbering");
    this._data.tagNumbering = structuredClone(value);
    this.changed("tagNumbering");
  }
  products!: C1_Products;

  get floors(): C1_Floor[] {
    return globalThis.state.floors.asArray().flatMap((floor) => (floor.c1 ? [floor.c1] : []));
  }

  get className(): string {
    return "C1_Project";
  }

  override getPersistentProperties(): string[] {
    return ["_data", "settings", ...super.getPersistentProperties()];
  }

  /** Loads only the declared project schema, keeping runtime adapters outside persistence. */
  override fromPersistentData(data: Record<string, unknown>): void {
    const persistentData: Record<string, unknown> = {};
    for (const name of this.getPersistentProperties()) {
      if (Object.prototype.hasOwnProperty.call(data, name)) persistentData[name] = data[name];
    }
    // Alte C1-Namen werden ignoriert; ausschließlich ProjectMetadata verwaltet den Namen.
    if (persistentData._data && typeof persistentData._data === "object") {
      const { projectName: _ignoredName, ...projectData } = persistentData._data as Record<string, unknown>;
      persistentData._data = projectData;
    }
    super.fromPersistentData(persistentData);
  }

  static fromJSON(input: unknown, templateGroups: BaseModelMap<TemplateGroup>): C1_Project {
    const project = new C1_Project(
      {
        updatedAt: new Date().toISOString(),
        northAngle: 0,
        houseType: "detachedHouse",
        buildingWidth: 12,
        buildingLength: 12,
        floorDistance: 3,
        equipmentStandard: 4,
        selectedProductManufacturers: {},
      },
      templateGroups,
    );
    project.fromJSON(input);
    return project;
  }

  get projectName() {
    return this.projectMetadata.name;
  }

  get updatedAt() {
    return this.data.updatedAt;
  }

  get northAngle() {
    return this.data.northAngle;
  }

  equipmentPostitionGeometric(id: string): { x: number; y: number } | undefined {
    for (const floor of this.floors) {
      for (const room of floor.rooms) {
        const opening = room.openings.find((candidate) => candidate.id === id);
        if (opening) return opening.equipmentPostitionGeometric(id);

        for (const furniture of room.furnitures) {
          if (furniture.id === id) return furniture.equipmentPostitionGeometric(id);
        }
      }
    }
    return undefined;
  }

  get houseType() {
    return this.data.houseType;
  }

  get buildingWidth() {
    return this.data.buildingWidth;
  }

  get buildingLength() {
    return this.data.buildingLength;
  }

  get floorDistance() {
    return this.data.floorDistance;
  }

  get equipmentStandard() {
    return this.data.equipmentStandard;
  }

  get selectedProductManufacturers() {
    return this.data.selectedProductManufacturers;
  }

  get masterPdfConfig(): C1_MasterPdfConfigData | undefined {
    return this.data.masterPdfConfig ? structuredClone(this.data.masterPdfConfig) : undefined;
  }

  setMasterPdfConfig(masterPdfConfig: C1_MasterPdfConfigData) {
    this.beforeChange("masterPdfConfig");
    this._data.masterPdfConfig = structuredClone(masterPdfConfig);
    this.changed("masterPdfConfig");
  }

  setSelectedProductManufacturers(selectedProductManufacturers: C1_SelectedProductManufacturers) {
    this.beforeChange("selectedProductManufacturers");
    this._data.selectedProductManufacturers = { ...selectedProductManufacturers };
    this.changed("selectedProductManufacturers");
  }

  get bounds(): Rect {
    return calculateProjectBounds(state.axes, this.floors.flatMap(floor => [
      ...floor.rooms.flatMap(room => room.polygonPoints()),
      ...(floor.freeWalls ?? []).flatMap(wall => [wall.start, wall.end]),
    ]));
  }

  boundsForFloor(floorId: string): Rect {
    const floor = this.getFloor(floorId);
    return calculateProjectBounds(this.axesForFloor(floorId), [
      ...(floor?.rooms.flatMap(room => room.polygonPoints()) ?? []),
      ...(floor?.freeWalls ?? []).flatMap(wall => [wall.start, wall.end]),
    ]);
  }

  setProjectName(projectName: string) {
    if (projectName !== this.projectMetadata.name) {
      this.projectMetadata.name = projectName;
    }
  }

  setProjectDetails(details: {
    projectName: string;
    houseType: C1_HouseType;
    buildingWidth: number;
    buildingLength: number;
    floorDistance: number;
    equipmentStandard: C1_EquipmentStandard;
  }) {
    this.beforeChange("projectDetails");
    this.setProjectName(details.projectName);
    this._data.houseType = details.houseType;
    this._data.buildingWidth = details.buildingWidth;
    this._data.buildingLength = details.buildingLength;
    this._data.floorDistance = details.floorDistance;
    this._data.equipmentStandard = details.equipmentStandard;
    this.changed("projectDetails");
  }

  setNorthAngle(northAngle: number) {
    const roundedNorthAngle = ((Math.round(northAngle) % 360) + 360) % 360;
    if (roundedNorthAngle !== this.data.northAngle) {
      this.beforeChange("northAngle");
      this._data.northAngle = roundedNorthAngle;
      this.changed("northAngle");
    }
  }

  updateBuildingDimensionsFromAxes() {
    const verticalAxes = state.axes.filter((axis) => axis.kind === "v").sort((left, right) => left.pos - right.pos);
    const horizontalAxes = state.axes.filter((axis) => axis.kind === "h").sort((left, right) => left.pos - right.pos);
    const lastVerticalAxis = verticalAxes[verticalAxes.length - 1];
    const lastHorizontalAxis = horizontalAxes[horizontalAxes.length - 1];

    if (lastVerticalAxis) {
      this._data.buildingWidth = Math.round(lastVerticalAxis.pos * 10) / 10;
    }
    if (lastHorizontalAxis) {
      this._data.buildingLength = Math.round(lastHorizontalAxis.pos * 10) / 10;
    }
  }

  markSaved(date = new Date()) {
    this._data.updatedAt = date.toISOString();
  }

  fromJSON(source: any) {
    if (!source.settings) {
      throw new Error("Invalid project JSON");
    }

    const sourceAxes: C1_AxisData[] | undefined = Array.isArray(source.axes) ? source.axes : undefined;
    const initialAxisLabels = axisLabels(sourceAxes ?? []);

    if (source.id) this.id = source.id;
    this._data.updatedAt = source.updatedAt ?? this._data.updatedAt;
    this._data.northAngle = source.northAngle ?? this._data.northAngle;
    this._data.houseType = source.houseType ?? this._data.houseType;
    this._data.buildingWidth = source.buildingWidth ?? this._data.buildingWidth;
    this._data.buildingLength = source.buildingLength ?? this._data.buildingLength;
    this._data.floorDistance = source.floorDistance ?? this._data.floorDistance;
    this._data.equipmentStandard = source.equipmentStandard ?? this._data.equipmentStandard;
    this._data.selectedProductManufacturers = source.selectedProductManufacturers ?? {};
    this._data.tagNumbering = source.tagNumbering ? structuredClone(source.tagNumbering) : undefined;
    const { modelSettings, pdfConfig } = migrateC1PdfSettings(source.settings, source.masterPdfConfig);
    this._data.masterPdfConfig = pdfConfig;
    this.settings = new C1_ModelSettings(modelSettings, this);
    let migratedAxisIds = new Map<string, string>();
    if (sourceAxes) {
      const normalizedAxes: C1_AxisData[] = [];
      for (const axis of sourceAxes) {
        normalizedAxes.push({ ...axis, label: initialAxisLabels.get(axis.id) });
      }
      migratedAxisIds = this.replaceAxes(normalizedAxes);
    }
    this.changed("project");
    return migratedAxisIds;
  }

  getFloor(id: string) {
    return this.floors.find((floor) => floor.id === id);
  }

  addFloor(data: C1_FloorData & { name: string }) {
    const floors = globalThis.state.floors;
    const floor = new Floor({ name: data.name }, floors);
    floor.c1 = new C1_Floor(data, floor);
    floors.add(floor);
    return floor.c1;
  }

  /** Creates the ground floor used when a project does not contain any floors yet. */
  createDefaultFloor(): C1_Floor {
    return this.addFloor({ name: "Erdgeschoss", level: 0, ownCabinet: false });
  }

  removeFloor(id: string) {
    const floors = globalThis.state.floors;
    const floor = floors.findFirst((candidate) => candidate.id === id);
    if (floors.size <= 1 || !floor) return false;
    floor.delete();
    return true;
  }

  get floorsHaveIndividualAxes(): boolean { return state.settings.floorsHaveIndividualAxes; }

  axesForFloor(floorId: string): C1_Axis[] {
    return axesForFloor(state.axes.asArray(), this.getFloor(floorId), state.settings.floorsHaveIndividualAxes);
  }

  /** Preserve every floor's visible grid before adding a floor with independent PDF coordinates. */
  enableIndividualFloorAxes(): void {
    if (state.settings.floorsHaveIndividualAxes) return;
    const axes = state.axes.asArray();
    const floors = this.floors;
    for (const axis of axes) {
      for (const floor of floors.slice(1)) {
        this.addAxis({ ...axis.toJSON(), id: undefined, floorId: floor.id });
      }
      if (floors[0]) axis.setFloorId(floors[0].id);
    }
    state.settings.floorsHaveIndividualAxes = true;
  }

  /** Split legacy shared axes before editing so other floors retain their coordinates. */
  isolateAxisForFloor(axis: C1_Axis, floorId: string): C1_Axis {
    if (!state.settings.floorsHaveIndividualAxes || axis.floorId === floorId) return axis;
    if (axis.floorId) return this.addAxis({ ...axis.toJSON(), floorId, id: undefined });
    for (const floor of floorsForAxis(axis, this.floors, true)) {
      if (floor.id !== floorId) this.addAxis({ ...axis.toJSON(), floorId: floor.id, id: undefined });
    }
    axis.setFloorId(floorId);
    return axis;
  }

  addAxis(data: WithOptionalId<C1_AxisData>) {
    this.beforeChange("axes");
    const floorId = state.settings.floorsHaveIndividualAxes ? data.floorId ?? c1_state.currentFloorId.value : data.floorId;
    const axis = new C1_Axis({ ...data, ...(floorId ? {floorId} : {}) }, state.axes);
    state.axes.add(axis);
    this.relabelAxes();
    this.changed("axes");
    return axis;
  }

  removeAxis(id: string) {
    this.beforeChange("axes");
    const axis = state.axes.findFirst((candidate) => candidate.id === id);
    if (axis) axis.delete();
    this.relabelAxes();
    this.changed("axes");
  }

  replaceAxes(data: WithOptionalId<C1_AxisData>[]): Map<string, string> {
    this.beforeChange("axes");
    const retainedAxes = new Set<C1_Axis>();
    const migratedIds = new Map<string, string>();
    for (const axisData of data) {
      let axis = axisData.id ? state.axes.findFirst((candidate) => candidate.id === axisData.id) : undefined;
      if (!axis) {
        axis = state.axes.findFirst((candidate) => candidate.kind === axisData.kind && candidate.floorId === axisData.floorId && !retainedAxes.has(candidate));
      }
      if (!axis) {
        axis = new C1_Axis(axisData, state.axes);
        state.axes.add(axis);
      }
      retainedAxes.add(axis);
      if (axisData.id) migratedIds.set(axisData.id, axis.id);
      axis.setFloorId(axisData.floorId);
      axis.setPosition(axisData.pos);
      axis.setLocked(axisData.locked ?? true);
    }
    const obsoleteAxes = state.axes.asArray();
    for (const axis of obsoleteAxes) {
      if (!retainedAxes.has(axis)) axis.delete();
    }
    this.relabelAxes();
    this.changed("axes");
    return migratedIds;
  }

  cleanupUnusedAxes(preserveBoundaryAxes = true, floorId?: string) {
    const rooms = this.floors.flatMap((floor) => floor.rooms);
    const pdfFloorIds = new Set(this.floors.filter(floor => !!floor.pdfSource).map(floor => floor.id));
    const isPdfAxis = (axis: C1_Axis) => axis.data.pdfBoundary === true ||
      (axis.floorId ? pdfFloorIds.has(axis.floorId) : pdfFloorIds.size > 0 && !state.settings.floorsHaveIndividualAxes);

    if (!floorId && !rooms.length && !state.settings.floorsHaveIndividualAxes && !pdfFloorIds.size && !state.axes.findFirst(axis => axis.data.pdfBoundary === true)) {
      const resetBoundaryAxes = (kind: "v" | "h", endPosition: number) => {
        const axes = state.axes.filter((axis) => axis.kind === kind).sort((left, right) => left.pos - right.pos);
        return [
          axes[0] ? { ...axes[0].toJSON(), pos: 0 } : { kind, pos: 0 },
          axes.length > 1 ? { ...axes[axes.length - 1].toJSON(), pos: endPosition } : { kind, pos: endPosition },
        ];
      };

      this.replaceAxes([...resetBoundaryAxes("v", this.buildingWidth), ...resetBoundaryAxes("h", this.buildingLength)]);
      return;
    }

    const usedAxisIds = new Set<string>();
    for (const floor of this.floors) {
      const floorAxes = this.axesForFloor(floor.id);
      for (const room of floor.rooms) {
        for (const point of room.polygonPoints()) {
          for (const axis of floorAxes) {
            const coordinate = axis.kind === "v" ? point.x : point.y;
            if (Math.abs(axis.pos - coordinate) < 0.001) usedAxisIds.add(axis.id);
          }
        }
      }
    }
    const boundaryAxisIds = new Set<string>();
    if (preserveBoundaryAxes && !state.settings.floorsHaveIndividualAxes) {
      for (const kind of ["v", "h"] as const) {
        const axes = state.axes.filter((axis) => axis.kind === kind && !isPdfAxis(axis)).sort((left, right) => left.pos - right.pos);
        if (!axes.length) continue;
        boundaryAxisIds.add(axes[0].id);
        boundaryAxisIds.add(axes[axes.length - 1].id);
      }
    }
    const obsoleteAxes = state.axes.filter((axis) => (!floorId || axis.floorId === floorId) && !usedAxisIds.has(axis.id) && !boundaryAxisIds.has(axis.id));
    for (const axis of obsoleteAxes) axis.delete();
    if (obsoleteAxes.length > 0) this.relabelAxes();
  }

  mergeNearbyAxes(keepAxisId: string, threshold = 0.1) {
    let keepAxis = state.axes.findFirst((axis) => axis.id === keepAxisId);
    if (!keepAxis) return;

    const floorId = keepAxis.floorId ?? c1_state.currentFloorId.value;
    keepAxis = this.isolateAxisForFloor(keepAxis, floorId);
    keepAxisId = keepAxis.id;
    while (true) {
      let closest = this.axesForFloor(floorId)
        .filter((axis) => axis.id !== keepAxis!.id && axis.kind === keepAxis!.kind)
        .map((axis) => ({ axis, distance: Math.abs(axis.pos - keepAxis!.pos) }))
        .filter(({ distance }) => distance < threshold)
        .sort((left, right) => left.distance - right.distance)[0]?.axis;
      if (!closest) break;

      closest = this.isolateAxisForFloor(closest, floorId);
      for (const floor of floorsForAxis(closest, this.floors, state.settings.floorsHaveIndividualAxes)) {
        for (const room of floor.rooms) room.moveAlignedPoints(closest.kind, closest.pos, keepAxis.pos);
      }
      this.removeAxis(closest.id);
      keepAxis = state.axes.findFirst((axis) => axis.id === keepAxisId);
      if (!keepAxis) return;
    }

    this.relabelAxes();
    this.recalculateWallRelations();
  }

  /** Merge closest parallel axes within each floor when floor coordinates are independent.
   * Distances and midpoints are in metres. Returns source IDs mapped to survivors.
   */
  mergeAxesAtMidpoints(threshold = 0.2, floorId?: string): Map<string, string> {
    const migratedIds = new Map<string, string>();
    for (const axis of state.axes) migratedIds.set(axis.id, axis.id);
    if (!Number.isFinite(threshold) || threshold <= 0) return migratedIds;
    if (state.settings.floorsHaveIndividualAxes && !floorId) {
      for (const floor of this.floors) {
        for (const [source, target] of this.mergeAxesAtMidpoints(threshold, floor.id)) {
          if (source !== target) migratedIds.set(source, target);
        }
      }
      return migratedIds;
    }
    const eligibleAxes = () => floorId && state.settings.floorsHaveIndividualAxes
      ? this.axesForFloor(floorId) : state.axes.asArray();
    let merged = false;
    while (true) {
      let pair: { keep: C1_Axis; discard: C1_Axis; distance: number } | undefined;
      for (const kind of ["v", "h"] as const) {
        const axes = eligibleAxes()
          .filter((axis) => axis.kind === kind && !axis.data.pdfBoundary)
          .sort((a, b) => a.pos - b.pos || a.id.localeCompare(b.id));
        for (let index = 1; index < axes.length; index++) {
          const keep = axes[index - 1],
            discard = axes[index];
          const distance = discard.pos - keep.pos;
          // Avoid merging exactly 20 cm because of floating-point rounding.
          if (distance >= threshold - 1e-9 || !Number.isFinite(distance)) continue;
          if (!pair || distance < pair.distance) pair = { keep, discard, distance };
        }
      }
      if (!pair) break;
      if (!merged) this.beforeChange("axes");
      merged = true;
      let { keep, discard } = pair;
      if (floorId) {
        keep = this.isolateAxisForFloor(keep, floorId);
        discard = this.isolateAxisForFloor(discard, floorId);
      }
      const midpoint = (keep.pos + discard.pos) / 2;
      keep.setPosition(midpoint);
      for (const floor of floorsForAxis(discard, this.floors, state.settings.floorsHaveIndividualAxes)) {
        for (const room of floor.rooms) room.moveAlignedPoints(discard.kind, discard.pos, midpoint);
      }
      for (const [sourceId, targetId] of migratedIds) {
        if (targetId === discard.id) migratedIds.set(sourceId, keep.id);
      }
      discard.delete();
    }
    if (merged) {
      this.relabelAxes();
      this.recalculateWallRelations();
      this.changed("axes");
    }
    return migratedIds;
  }

  /** Rebuilds wall partnerships and exterior classifications once per floor after axis geometry changes. */
  private recalculateWallRelations(): void {
    for (const floor of this.floors) {
      const firstRoom = floor.rooms[0];
      if (firstRoom) firstRoom.recalculateFloorWallTypes();
    }
  }

  private relabelAxes() {
    const axisData: C1_AxisData[] = [];
    for (const axis of state.axes) axisData.push(axis.toJSON());
    const labels = axisLabels(axisData);
    for (const axis of state.axes) axis.setLabel(labels.get(axis.id) ?? "");
  }

  toJSON(): C1_ProjectData {
    const jsonAxis: C1_AxisData[] = [];
    for (const axis of state.axes) jsonAxis.push(axis.toJSON());

    return {
      id: this.id,
      updatedAt: this.updatedAt,
      northAngle: this.northAngle,
      houseType: this.houseType,
      buildingWidth: this.buildingWidth,
      buildingLength: this.buildingLength,
      floorDistance: this.floorDistance,
      equipmentStandard: this.equipmentStandard,
      selectedProductManufacturers: this.selectedProductManufacturers,
      masterPdfConfig: this.masterPdfConfig,
      settings: this.settings.toJSON(),
      tagNumbering: this.data.tagNumbering,
      axes: jsonAxis,
    };
  }
}
